import "./helppage.scss";
import {
    type FC,
    Fragment,
    useEffect,
    useMemo,
    useState,
} from "react";
import { Select } from "@onshopfront/components";
import { useShopfrontDispatch } from "../../_Bootstrap/hooks";
import { hideDialog } from "../../Actions/Dialogs/DialogActions";
import FontAwesomeLight from "../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import { type HelpSectionArticle, useHelpSections } from "../../Utilities/Platform/HelpManager/HelpManager";
import Button from "../Buttons/Button";
import { ZendeskArticle } from "../Zendesk/ZendeskArticle";

interface ArticleOptionType {
    label: string;
    value: string;
    additional: {
        section: string;
    };
}

export const HelpPage: FC = () => {
    const dispatch = useShopfrontDispatch();

    const sections = useHelpSections();

    const articleMap = useMemo(() => {
        const results: Record<string, HelpSectionArticle> = {};

        for(const section of sections) {
            for(const article of section.articles) {
                results[`${section.title}|${article.zendeskId}`] = article;
            }
        }

        return results;
    }, [ sections ]);

    const [ currentPage, setCurrentPage ] = useState<null | HelpSectionArticle>(null);

    useEffect(() => {
        setCurrentPage(prevState => {
            if(!sections.length) {
                return null;
            } else if(prevState !== null) {
                return prevState;
            }

            for(let i = 0, l = sections.length; i < l; i++) {
                if(sections[i].articles.length) {
                    return sections[0].articles[0];
                }
            }

            return null;
        });
    }, [ sections ]);

    const onClose = () => {
        dispatch(hideDialog());
    };

    return (
        <div className="help-page w-[95vw] max-w-[theme(screens.lg)] h-[80vh] flex">
            <div
                className={
                    "hidden bg-neutral-100 dark:bg-dark-800 md:block md:w-1/3 md:min-w-[200px] overflow-auto"
                }
            >
                {sections.map((section, index) => (
                    <Fragment key={index}>
                        <div
                            className={
                                "text-lg p-4 opacity-80 w-full mt-2 first:mt-0 " +
                                "border-b border-b-neutral-600 dark:border-b-dark-border"
                            }
                        >
                            {section.title}
                        </div>
                        {section.articles.map(article => (
                            <button
                                key={article.zendeskId}
                                onClick={() => {
                                    setCurrentPage(article);
                                }}
                                className={
                                    "button text-left text-lg px-4 py-2 w-full " +
                                    "whitespace-nowrap text-ellipsis overflow-hidden " + (
                                        currentPage?.zendeskId === article.zendeskId ? (
                                            "bg-primary text-white hover:bg-primary-dark dark:bg-primary-dark " +
                                            "dark:hover:bg-primary"
                                        ) : (
                                            "bg-transparent hover:bg-black/10 " +
                                            "dark:hover:bg-dark-700 dark:text-white"
                                        )
                                    )
                                }
                            >
                                {article.name}
                            </button>
                        ))}
                    </Fragment>
                ))}
            </div>
            <div className="flex flex-col grow shrink">
                <div className="flex flex-row-reverse justify-between p-1">
                    <Button
                        type="button"
                        onClick={onClose}
                        classes={[ "button-neutral" ]}
                    >
                        <FontAwesomeLight icon="times" />Close
                    </Button>
                    <Select
                        className="max-w-[200px] md:hidden block"
                        options={sections.map(section => ({
                            label  : section.title,
                            options: section.articles.map(article => ({
                                label     : article.name,
                                value     : article.zendeskId,
                                additional: {
                                    section: section.title,
                                },
                            })),
                        }))}
                        value={currentPage ? {
                            label     : currentPage.name,
                            value     : currentPage.zendeskId,
                            additional: { section: "" },
                        } : null}
                        onChange={option => {
                            setCurrentPage(
                                articleMap[`${option.additional.section}|${option.value}`]
                            );
                        }}
                    />
                </div>
                {currentPage !== null && (
                    <div className="w-full flex-grow overflow-auto">
                        <ZendeskArticle
                            id={currentPage.zendeskId}
                            scrollAnchor={currentPage.scrollAnchor}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
